import { Info } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StateSelect } from '@/components/ui/StateSelect'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface CoiRequestValues {
  role: string
  name: string
  email: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
}

/** AddCertificateHolderDialog's customer-facing fields in its own shell, collecting nothing. */
export function AddCertificateHolderModal({
  id,
  isNamedParty = false,
  open,
  onOpenChange,
  onSave,
}: {
  id: string
  /** Adds a loss payee or additional insured, who gets named on the policy as well as the COI. */
  isNamedParty?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: () => void
}) {
  const { control, register, handleSubmit } = useForm<CoiRequestValues>()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isNamedParty ? 'Add loss payee or additional insured' : 'Add certificate holder'}
          </DialogTitle>
          <DialogDescription>
            We&apos;ll generate and send an official COI once the details of certificate holder are confirmed.
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSave)}>
          <FieldGroup>
            {isNamedParty && (
              <Field>
                <div className="flex items-center gap-1.5">
                  <Label htmlFor={`${id}-role`}>Add as</Label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* type=button: Button sets none, and an unset type submits the form. */}
                      <Button
                        type="button"
                        variant="iconOnly"
                        size="icon-sm"
                        aria-label="What loss payees and additional insureds are"
                        className="text-muted-foreground hover:text-foreground size-5"
                      >
                        <Info className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      A loss payee gets paid on a claim for equipment they financed or lease to you. An
                      additional insured is a company your policy also protects, like a shipper or facility.
                    </TooltipContent>
                  </Tooltip>
                </div>
                <Controller
                  name="role"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id={`${id}-role`}>
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="loss_payee">Loss payee</SelectItem>
                        <SelectItem value="additional_insured">Additional insured</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </Field>
            )}
            <Field>
              <Label htmlFor={`${id}-name`}>Legal business name</Label>
              <Input id={`${id}-name`} placeholder="C.H. Robinson Worldwide, Inc." {...register('name')} />
            </Field>
            <Field>
              <Label htmlFor={`${id}-email`}>Email</Label>
              <FieldDescription>
                Separate several addresses with commas. We&apos;ll send the COI to all of them.
              </FieldDescription>
              <Input
                id={`${id}-email`}
                placeholder="ap@broker.com, dispatch@broker.com"
                {...register('email')}
              />
            </Field>
            {/* AddressFields' layout without its autocomplete, which sends what's typed to Google Places. */}
            <Field>
              <Label>Address (optional)</Label>
              <div className="flex flex-col gap-3">
                <Input placeholder="Street address" aria-label="Street address" {...register('line1')} />
                <Input
                  placeholder="Street address 2 (optional)"
                  aria-label="Street address 2"
                  {...register('line2')}
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input placeholder="City" aria-label="City" {...register('city')} />
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <StateSelect value={field.value} onChange={field.onChange} error={false} clearable />
                    )}
                  />
                  <Input placeholder="Zip" aria-label="Zip" {...register('zip')} />
                </div>
              </div>
            </Field>
            <Field orientation="horizontal">
              <div className="flex-1">
                <Label className="text-md mb-1">Include trucks in vehicle schedule</Label>
                <FieldDescription className="text-sm">
                  Show your trucks on the ACORD 101 vehicle schedule page of this holder&apos;s COI.
                </FieldDescription>
              </div>
              <Switch />
            </Field>
            <Field orientation="horizontal">
              <div className="flex-1">
                <Label className="text-md mb-1">Include trailers in vehicle schedule</Label>
                <FieldDescription className="text-sm">
                  Show your trailers on the ACORD 101 vehicle schedule page of this holder&apos;s COI.
                </FieldDescription>
              </div>
              <Switch />
            </Field>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="link" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit">Save info</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
